/* ──────────────────────────────────────────────────────────────
   src/hooks/useAusencias.ts     (v3 – convert-to-vacation + reject)
   ────────────────────────────────────────────────────────────── */
import { useState, useCallback } from "react";
import api from "../api/axios";
import type { MotivoSlug } from "../constants/enums";

/* ====== Tipos ====== */
export interface Ausencia {
  id: number;
  fecha: string; // YYYY-MM-DD
  horaInicio?: string | null; // HH:mm | null ⇒ día completo
  horaFin?: string | null;

  tipo: "justificada" | "injustificada";
  estado: "pending" | "approved" | "rejected";
  idProfesor: number;
  motivo: {
    id: number;
    slug: MotivoSlug;
    colorHex: string;
    nombreEs: string;
    nombreEn: string;
  };

  nota?: string | null;

  /* metadatos de adjuntos */
  adjuntos?: {
    id: number;
    filename: string;
  }[];
}

/* —— payload para crear / actualizar —— */
export type AusenciaDTO = {
  fecha: string;
  horaInicio?: string | null;
  horaFin?: string | null;
  motivoId: number; // FK catálogo
  tipo: "justificada" | "injustificada";
  nota?: string | null;
};

/* ============================================================ */
export function useAusencias(idProfesor: number) {
  const [rows, setRows] = useState<Ausencia[]>([]);

  /* ---------- READ ---------- */
  const refresh = useCallback(async () => {
    if (!idProfesor) {
      setRows([]);
      return;
    }

    const { data } = await api.get<Ausencia[]>("/ausencias", {
      params: { profesor: idProfesor },
    });
    setRows(data);
  }, [idProfesor]);

  /* ---------- CREATE (back-office) ---------- */
  const add = async (dto: AusenciaDTO) => {
    await api.post("/ausencias", { ...dto, idProfesor });
    await refresh(); // nos devuelve la fila completa
  };

  /* ---------- UPDATE ---------- */
  const update = async (id: number, dto: Partial<AusenciaDTO>) => {
    const { data } = await api.put<Ausencia>(`/ausencias/${id}`, dto);
    setRows((s) => s.map((a) => (a.id === id ? data : a)));
    return data;
  };

  /* ---------- DELETE físico (back-office) ---------- */
  const remove = async (id: number) => {
    await api.delete(`/ausencias/${id}`);
    setRows((s) => s.filter((a) => a.id !== id));
  };

  /* ---------- RECHAZAR / cancelar solicitud (pendiente) ---------- */
  const reject = async (id: number) => {
    await api.delete(`/ausencias/${id}/cancelar`);
    setRows((s) => s.filter((a) => a.id !== id));
  };

  /* ---------- CONVERTIR EN VACACIONES ---------- */
  const convert = async (id: number) => {
    await api.post(`/ausencias/${id}/convertir-vacacion`);
    // la ausencia desaparece → la quitamos del estado
    setRows((s) => s.filter((a) => a.id !== id));
  };

  return {
    rows,
    refresh,
    add,
    update,
    remove, // delete definitivo
    reject, // “Rechazar” (cancelar solicitud)  = DELETE /cancelar
    convert, // “Convertir en vacaciones”
  };
}
